/**
 * Stale synthetic iCal “ghost” rows (legacy airbnb_PROP_dates ids with no real UID).
 * Categories match the cleanup inventory:
 *   A — same slot as a real modern iCal (safest delete)
 *   B — same slot as Export/Manual only
 *   C — orphan (no same-date twin)
 */

import { isManualOrExportSource } from './bookingSources';

export type StaleIcalGhostCategory = 'A' | 'B' | 'C';

export type GhostClassifiableBooking = {
  id: number;
  source?: string | null;
  property_id?: number | null;
  check_in_date?: string | null;
  check_out_date?: string | null;
  external_booking_id?: string | null;
  ical_uid?: string | null;
  reservation_url?: string | null;
  guest_name?: string | null;
  status?: string | null;
  is_merged_pair?: boolean;
};

const SYNTHETIC_EXT_RE = /^airbnb_[A-Za-z0-9]+_\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/;

function ymd(value?: string | null): string {
  if (!value) return '';
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}

export function isSyntheticGhostIcal(booking: GhostClassifiableBooking): boolean {
  if (booking.is_merged_pair) return false;
  if (String(booking.source || '').trim() !== 'iCal') return false;
  const uid = String(booking.ical_uid || '').trim();
  const ext = String(booking.external_booking_id || '').trim();
  const url = String(booking.reservation_url || '').trim();
  const hasRealUid = Boolean(uid) || ext.includes('@');
  if (hasRealUid && !SYNTHETIC_EXT_RE.test(ext)) return false;
  if (SYNTHETIC_EXT_RE.test(ext)) return true;
  // Legacy empty shells: no uid, no url, no @ in external id
  return !hasRealUid && !url;
}

function slotKey(b: GhostClassifiableBooking): string {
  return `${b.property_id ?? ''}|${ymd(b.check_in_date)}|${ymd(b.check_out_date)}`;
}

/** Map booking id → A/B/C for every synthetic ghost in the set. */
export function classifyStaleIcalGhosts(
  bookings: GhostClassifiableBooking[]
): Map<number, StaleIcalGhostCategory> {
  const bySlot = new Map<string, GhostClassifiableBooking[]>();
  bookings.forEach((b) => {
    const key = slotKey(b);
    if (!key.startsWith('|') && !key.includes('||')) {
      // allow missing dates still — group them
    }
    const list = bySlot.get(key) || [];
    list.push(b);
    bySlot.set(key, list);
  });

  const out = new Map<number, StaleIcalGhostCategory>();
  bySlot.forEach((group) => {
    const ghosts = group.filter(isSyntheticGhostIcal);
    if (!ghosts.length) return;
    const hasRealIcal = group.some(
      (b) => String(b.source || '').trim() === 'iCal' && !isSyntheticGhostIcal(b)
    );
    const hasExport = group.some((b) => {
      const s = String(b.source || '').trim();
      return isManualOrExportSource(s) || s === 'Merged' || Boolean(b.is_merged_pair);
    });
    ghosts.forEach((g) => {
      if (hasRealIcal) out.set(g.id, 'A');
      else if (hasExport) out.set(g.id, 'B');
      else out.set(g.id, 'C');
    });
  });
  return out;
}

/** Soft row backgrounds — distinct per category. */
export const STALE_ICAL_GHOST_ROW_SX: Record<
  StaleIcalGhostCategory,
  { bgcolor: string; '&:hover': { bgcolor: string } }
> = {
  A: { bgcolor: 'rgba(255, 167, 38, 0.22)', '&:hover': { bgcolor: 'rgba(255, 167, 38, 0.34)' } }, // amber
  B: { bgcolor: 'rgba(41, 182, 246, 0.20)', '&:hover': { bgcolor: 'rgba(41, 182, 246, 0.32)' } }, // light blue
  C: { bgcolor: 'rgba(240, 98, 146, 0.18)', '&:hover': { bgcolor: 'rgba(240, 98, 146, 0.30)' } }, // pink
};

export const STALE_ICAL_GHOST_LEGEND: Array<{
  category: StaleIcalGhostCategory;
  label: string;
  swatch: string;
}> = [
  {
    category: 'A',
    label: 'A — ghost + real iCal twin (safe delete)',
    swatch: 'rgba(255, 167, 38, 0.45)',
  },
  {
    category: 'B',
    label: 'B — ghost + Export only',
    swatch: 'rgba(41, 182, 246, 0.40)',
  },
  {
    category: 'C',
    label: 'C — orphan ghost (review)',
    swatch: 'rgba(240, 98, 146, 0.40)',
  },
];

function parseYmd(value?: string | null): { t: number; ymd: string } | null {
  const s = ymd(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  return { t: Date.UTC(y, m - 1, d), ymd: s };
}

/**
 * Guest-less iCal “block” that fully contains ≥2 other real stays on the same property
 * (e.g. 28LC #7657 nesting several HM bookings). Hide these from the stay calendar.
 */
export function isNestingPhantomIcal(
  booking: GhostClassifiableBooking,
  all: GhostClassifiableBooking[]
): boolean {
  if (booking.is_merged_pair) return false;
  if (String(booking.source || '').trim() !== 'iCal') return false;
  if (isSyntheticGhostIcal(booking)) return false;
  if (String(booking.guest_name || '').trim()) return false;
  const range = parseYmd(booking.check_in_date);
  const rangeOut = parseYmd(booking.check_out_date);
  if (!range || !rangeOut || rangeOut.t <= range.t) return false;

  let nested = 0;
  for (let i = 0; i < all.length; i += 1) {
    const o = all[i];
    if (o.id === booking.id) continue;
    if (o.property_id !== booking.property_id) continue;
    if (o.is_merged_pair) {
      // merged counts as a real stay
    } else {
      const src = String(o.source || '').trim();
      if (src !== 'iCal' && !isManualOrExportSource(src) && src !== 'Merged') continue;
      if (src === 'iCal' && isSyntheticGhostIcal(o)) continue;
    }
    const status = String(o.status || '').trim().toLowerCase();
    if (status === 'cancelled') continue;
    const oi = parseYmd(o.check_in_date);
    const oo = parseYmd(o.check_out_date);
    if (!oi || !oo) continue;
    // Contained within [check_in, check_out) of the outer block
    if (oi.t >= range.t && oo.t <= rangeOut.t && oo.t > oi.t) {
      nested += 1;
      if (nested >= 2) return true;
    }
  }
  return false;
}

/** True when a booking should not be drawn on the stay-bar calendar. */
export function shouldHideFromStayCalendar(
  booking: GhostClassifiableBooking,
  all: GhostClassifiableBooking[],
  nestingRemnantIds?: Map<number, true> | Set<number>
): boolean {
  if (isSyntheticGhostIcal(booking)) return true;
  if (isNestingPhantomIcal(booking, all)) return true;
  if (nestingRemnantIds) {
    if (nestingRemnantIds instanceof Map) {
      if (nestingRemnantIds.has(booking.id)) return true;
    } else if (nestingRemnantIds.has(booking.id)) {
      return true;
    }
  }
  return false;
}

export function filterBookingsForStayCalendar<T extends GhostClassifiableBooking>(
  bookings: T[],
  nestingRemnantIds?: Map<number, true> | Set<number>
): T[] {
  return bookings.filter((b) => !shouldHideFromStayCalendar(b, bookings, nestingRemnantIds));
}
